/**
 * Equipaciones y nombre de equipo.
 *
 * Lo que hace que un partido sea TUYO no es el dorsal: es abrir el juego y ver
 * salir a los tuyos con sus colores y su nombre en el acta. Aqui viven las dos
 * cosas — la camiseta (colores + patron) y como se llama el equipo.
 *
 * Los preajustes se llaman por su COMBINACION DE COLORES, no por el club:
 * "Verdiblanco", "Grana y Azul", "Rojiblanco". Es a proposito y por dos
 * razones. La primera, que los nombres y escudos de los clubes son marcas
 * ajenas y no nos toca repartirlas. La segunda, que asi se cubre todo de una
 * vez: hay muchisimos equipos verdiblancos en el mundo, y con esta lista mas el
 * nombre libre cualquiera arma el suyo — el de Primera y el del barrio.
 *
 * Solo afecta al RENDER. Nada de aqui entra en la simulacion, asi que dos
 * clientes con equipaciones distintas siguen jugando el mismo partido bit a
 * bit: los colores nunca pueden desincronizar el online.
 */
import type { TeamSide } from "./types.js";

/**
 * Como se reparten los dos colores sobre la camiseta.
 *
 * Los cuatro ultimos (cuartos, galon, franja y cruz) llegaron con la carrera
 * (2026-09-26): con cuarenta y tantas camisetas, cinco cortes se quedaban
 * cortos para que cada club se distinguiera de un vistazo.
 */
export type KitPattern =
  | "plain"
  | "stripes"
  | "halves"
  | "sash"
  | "hoops"
  | "quarters"
  | "chevron"
  | "band"
  | "cross";

export interface Kit {
  id: string;
  /** Nombre de la combinacion, no del club. */
  label: string;
  /** Color dominante de la camiseta. */
  primary: number;
  /** Segundo color: franjas, mitad, banda o aros. `plain` lo ignora. */
  secondary: number;
  /** Detalles vivos: visor, nucleo, emblema, estela. */
  accent: number;
  /** Color del dorsal. Tiene que contrastar con `primary`. */
  ink: number;
  /** Camiseta del portero: siempre distinta de las dos de campo. */
  keeper: number;
  pattern: KitPattern;
}

export const DEFAULT_HOME_KIT = "carmesi";
export const DEFAULT_AWAY_KIT = "ultramar";

/**
 * El catalogo.
 *
 * Estan las combinaciones que de verdad se piden —las de las ligas grandes y
 * las de las selecciones— cada una con su patron, porque media identidad de una
 * camiseta esta en si va a rayas, a mitades o con banda cruzada.
 */
export const KITS: Record<string, Kit> = {
  carmesi: {
    id: "carmesi",
    label: "Carmesí",
    primary: 0xe5484d,
    secondary: 0xffffff,
    accent: 0xff8a8e,
    ink: 0xffffff,
    keeper: 0x2fbf71,
    pattern: "plain",
  },
  ultramar: {
    id: "ultramar",
    label: "Ultramar",
    primary: 0x3e63dd,
    secondary: 0xffffff,
    accent: 0x8fa8ff,
    ink: 0xffffff,
    keeper: 0xf4b942,
    pattern: "plain",
  },
  verdiblanco: {
    id: "verdiblanco",
    label: "Verdiblanco",
    primary: 0x0f9d58,
    secondary: 0xf5f7f4,
    accent: 0x6ff0a8,
    ink: 0xffffff,
    keeper: 0x1b1b1f,
    pattern: "stripes",
  },
  granaAzul: {
    id: "granaAzul",
    label: "Grana y Azul",
    primary: 0x8d1740,
    secondary: 0x1a3a8f,
    accent: 0xf2c14e,
    ink: 0xf7e7a8,
    keeper: 0x2fbf71,
    pattern: "stripes",
  },
  rojiblanco: {
    id: "rojiblanco",
    label: "Rojiblanco",
    primary: 0xd7222b,
    secondary: 0xf6f4f0,
    accent: 0x1b3a8f,
    ink: 0xffffff,
    keeper: 0xf4b942,
    pattern: "stripes",
  },
  /*
   * Rojinegro a listas: la del Ytuano FC de la carrera, guiño al Ituano de
   * Itu (el Galo). Solo se consigue jugando con ellos una temporada.
   */
  rojinegro: {
    id: "rojinegro",
    label: "Rojinegro",
    primary: 0xd7222b,
    secondary: 0x14141a,
    accent: 0xff8a8e,
    ink: 0xffffff,
    keeper: 0xf4b942,
    pattern: "stripes",
  },
  blanquinegro: {
    id: "blanquinegro",
    label: "Blanquinegro",
    primary: 0xf2f2ee,
    secondary: 0x14141a,
    accent: 0x14141a,
    ink: 0x14141a,
    keeper: 0xf27023,
    pattern: "stripes",
  },
  blancoOro: {
    id: "blancoOro",
    label: "Blanco y Oro",
    primary: 0xf6f6f2,
    secondary: 0xe8c66a,
    accent: 0xd4af37,
    ink: 0x1d2a3a,
    keeper: 0x1f7a5c,
    pattern: "plain",
  },
  negroOro: {
    id: "negroOro",
    label: "Negro y Oro",
    primary: 0x14141a,
    secondary: 0xe8c66a,
    accent: 0xf2c14e,
    ink: 0xf2c14e,
    keeper: 0xd7222b,
    pattern: "plain",
  },
  amarilloNegro: {
    id: "amarilloNegro",
    label: "Amarillo y Negro",
    primary: 0xf5d90a,
    secondary: 0x14141a,
    accent: 0x14141a,
    ink: 0x14141a,
    keeper: 0x0f9d58,
    pattern: "hoops",
  },
  celeste: {
    id: "celeste",
    label: "Celeste",
    primary: 0x7cc4ee,
    secondary: 0xf6f6f2,
    accent: 0x0b2a5b,
    ink: 0x0b2a5b,
    keeper: 0xd7222b,
    pattern: "plain",
  },
  naranja: {
    id: "naranja",
    label: "Naranja Total",
    primary: 0xf27023,
    secondary: 0x14141a,
    accent: 0xffc07a,
    ink: 0xffffff,
    keeper: 0x1a3a8f,
    pattern: "plain",
  },
  bandaRoja: {
    id: "bandaRoja",
    label: "Banda Roja",
    primary: 0xf6f6f2,
    secondary: 0xd7222b,
    accent: 0xd7222b,
    ink: 0x14141a,
    keeper: 0x14141a,
    pattern: "sash",
  },
  arosVerdes: {
    id: "arosVerdes",
    label: "Aros Verdes",
    primary: 0xf6f6f2,
    secondary: 0x0f7a41,
    accent: 0x0f7a41,
    ink: 0x14141a,
    keeper: 0xf5d90a,
    pattern: "hoops",
  },
  mitadesAzulGrana: {
    id: "mitadesAzulGrana",
    label: "Mitades",
    primary: 0x1a3a8f,
    secondary: 0x8d1740,
    accent: 0xf2c14e,
    ink: 0xffffff,
    keeper: 0x2fbf71,
    pattern: "halves",
  },
  violeta: {
    id: "violeta",
    label: "Violeta",
    primary: 0x6b3fa0,
    secondary: 0xf6f6f2,
    accent: 0xc9a6f2,
    ink: 0xffffff,
    keeper: 0xf5d90a,
    pattern: "plain",
  },
  granate: {
    id: "granate",
    label: "Granate",
    primary: 0x6d1a2e,
    secondary: 0xf2c14e,
    accent: 0xf2c14e,
    ink: 0xf7e7a8,
    keeper: 0x7cc4ee,
    pattern: "plain",
  },
  /*
   * La tanda de la carrera (2026-09-26). Siguen la misma regla que las de
   * arriba: se llaman por lo que parecen, no por ningun club. Solo el rojo y
   * el azul se tienen de salida; el resto se gana jugando la carrera, y lo que
   * se gana alli se viste tambien online.
   */
  tiburon: {
    id: "tiburon",
    label: "Tiburón",
    primary: 0x5c6670,
    secondary: 0xf2f2ee,
    accent: 0x9aa6b0,
    ink: 0xffffff,
    keeper: 0xf27023,
    pattern: "chevron",
  },
  pistacho: {
    id: "pistacho",
    label: "Pistacho",
    primary: 0x9bc53d,
    secondary: 0xf6f1d9,
    accent: 0x4a6b1a,
    ink: 0x14141a,
    keeper: 0x6b3fa0,
    pattern: "plain",
  },
  chicle: {
    id: "chicle",
    label: "Rosa Chicle",
    primary: 0xf06292,
    secondary: 0x14141a,
    accent: 0xffc1d6,
    ink: 0x14141a,
    keeper: 0x0f9d58,
    pattern: "band",
  },
  menta: {
    id: "menta",
    label: "Menta y Marino",
    primary: 0x7fdcc0,
    secondary: 0x13294b,
    accent: 0x13294b,
    ink: 0x14141a,
    keeper: 0xd7222b,
    pattern: "hoops",
  },
  burdeosOro: {
    id: "burdeosOro",
    label: "Burdeos y Oro",
    primary: 0x7a1f3d,
    secondary: 0xe8c66a,
    accent: 0xe8c66a,
    ink: 0xffffff,
    keeper: 0x7cc4ee,
    pattern: "quarters",
  },
  mostaza: {
    id: "mostaza",
    label: "Mostaza y Café",
    primary: 0xd9a520,
    secondary: 0x5a3a1e,
    accent: 0x5a3a1e,
    ink: 0x14141a,
    keeper: 0x1a3a8f,
    pattern: "halves",
  },
  arlequin: {
    id: "arlequin",
    label: "Arlequín",
    primary: 0x6b3fa0,
    secondary: 0xf5d90a,
    accent: 0xf5d90a,
    ink: 0xffffff,
    keeper: 0x0f9d58,
    pattern: "quarters",
  },
  vikingo: {
    id: "vikingo",
    label: "Vikingo",
    primary: 0x13294b,
    secondary: 0xd7222b,
    accent: 0xf6f6f2,
    ink: 0xffffff,
    keeper: 0xf5d90a,
    pattern: "cross",
  },
  nordico: {
    id: "nordico",
    label: "Nórdico",
    primary: 0xf6f6f2,
    secondary: 0x1a5fb4,
    accent: 0x1a5fb4,
    ink: 0x14141a,
    keeper: 0xd7222b,
    pattern: "cross",
  },
  lava: {
    id: "lava",
    label: "Lava",
    primary: 0x1b1b1f,
    secondary: 0xff6a1a,
    accent: 0xff6a1a,
    ink: 0xffffff,
    keeper: 0x7cc4ee,
    pattern: "chevron",
  },
  cieloNube: {
    id: "cieloNube",
    label: "Cielo y Nube",
    primary: 0x5fb0f0,
    secondary: 0xf6f6f2,
    accent: 0x0b2a5b,
    ink: 0x14141a,
    keeper: 0xf27023,
    pattern: "hoops",
  },
  petroleo: {
    id: "petroleo",
    label: "Petróleo y Lima",
    primary: 0x0e4f5c,
    secondary: 0xb8e62e,
    accent: 0xb8e62e,
    ink: 0xffffff,
    keeper: 0xf06292,
    pattern: "sash",
  },
  retro70: {
    id: "retro70",
    label: "Retro 70",
    primary: 0x7a4a26,
    secondary: 0xf2a33a,
    accent: 0xf6e8c8,
    ink: 0xffffff,
    keeper: 0x5fb0f0,
    pattern: "band",
  },
  radiactivo: {
    id: "radiactivo",
    label: "Radiactivo",
    primary: 0x39e75f,
    secondary: 0x14141a,
    accent: 0x14141a,
    ink: 0x14141a,
    keeper: 0xf06292,
    pattern: "stripes",
  },
  uva: {
    id: "uva",
    label: "Uva",
    primary: 0x4b1f5c,
    secondary: 0x8bc34a,
    accent: 0x8bc34a,
    ink: 0xffffff,
    keeper: 0xf5d90a,
    pattern: "sash",
  },
  salmon: {
    id: "salmon",
    label: "Salmón y Marino",
    primary: 0xf4907a,
    secondary: 0x13294b,
    accent: 0x13294b,
    ink: 0x14141a,
    keeper: 0x2fbf71,
    pattern: "hoops",
  },
  carbonBrasa: {
    id: "carbonBrasa",
    label: "Carbón y Brasa",
    primary: 0x2b2b30,
    secondary: 0xe5484d,
    accent: 0xe5484d,
    ink: 0xffffff,
    keeper: 0xf5d90a,
    pattern: "band",
  },
  sandia: {
    id: "sandia",
    label: "Sandía",
    primary: 0xef5a6f,
    secondary: 0x1f8a4c,
    accent: 0x1f8a4c,
    ink: 0xffffff,
    keeper: 0x13294b,
    pattern: "hoops",
  },
  tricolor: {
    id: "tricolor",
    label: "Tricolor",
    primary: 0x1a3a8f,
    secondary: 0xf6f6f2,
    accent: 0xd7222b,
    ink: 0xffffff,
    keeper: 0xf5d90a,
    pattern: "band",
  },
  desierto: {
    id: "desierto",
    label: "Desierto",
    primary: 0xe0c48f,
    secondary: 0xb5532e,
    accent: 0xb5532e,
    ink: 0x14141a,
    keeper: 0x13294b,
    pattern: "quarters",
  },
  pinguino: {
    id: "pinguino",
    label: "Pingüino",
    primary: 0x14141a,
    secondary: 0xf6f6f2,
    accent: 0xf2a33a,
    ink: 0xffffff,
    keeper: 0xf5d90a,
    pattern: "halves",
  },
  electrico: {
    id: "electrico",
    label: "Azul Eléctrico",
    primary: 0x1f5fff,
    secondary: 0xf5d90a,
    accent: 0xf5d90a,
    ink: 0xffffff,
    keeper: 0xef5a6f,
    pattern: "sash",
  },
  cereza: {
    id: "cereza",
    label: "Cereza",
    primary: 0xb5122e,
    secondary: 0xf6f6f2,
    accent: 0xf6f6f2,
    ink: 0xffffff,
    keeper: 0x2fbf71,
    pattern: "chevron",
  },
  bosque: {
    id: "bosque",
    label: "Bosque",
    primary: 0x1f4d2b,
    secondary: 0xd8c8a0,
    accent: 0xd8c8a0,
    ink: 0xffffff,
    keeper: 0xf27023,
    pattern: "band",
  },
  hielo: {
    id: "hielo",
    label: "Hielo",
    primary: 0xe8f4fb,
    secondary: 0x7cc4ee,
    accent: 0x2a6f97,
    ink: 0x14141a,
    keeper: 0xd7222b,
    pattern: "cross",
  },
  sangreArena: {
    id: "sangreArena",
    label: "Sangre y Arena",
    primary: 0xb3202a,
    secondary: 0xe0c48f,
    accent: 0xe0c48f,
    ink: 0xffffff,
    keeper: 0x13294b,
    pattern: "halves",
  },
  medianoche: {
    id: "medianoche",
    label: "Medianoche",
    primary: 0x0f1a3a,
    secondary: 0x8fa8ff,
    accent: 0x8fa8ff,
    ink: 0xffffff,
    keeper: 0xf27023,
    pattern: "chevron",
  },
  coral: {
    id: "coral",
    label: "Coral",
    primary: 0xff7f50,
    secondary: 0xf6f6f2,
    accent: 0x13294b,
    ink: 0x14141a,
    keeper: 0x0e4f5c,
    pattern: "stripes",
  },

  // --- De competicion (2026-09-27): solo se ganan en las estadisticas mundiales.
  // AL FINAL del catalogo a proposito: la carrera reparte las equipaciones a sus
  // clubes en el orden del catalogo, y meterlas en medio cambiaria los clubes de
  // las carreras ya empezadas (lo vigila la huella de `WORLD_VERSION`).
  temporadaOro: {
    id: "temporadaOro",
    label: "Temporada Oro",
    primary: 0x14141a,
    secondary: 0xffd35a,
    accent: 0xffe9a6,
    ink: 0xffd35a,
    keeper: 0x7a2fd0,
    pattern: "chevron",
  },
  temporadaPlatino: {
    id: "temporadaPlatino",
    label: "Temporada Platino",
    primary: 0x0f3d3a,
    secondary: 0x6fe0d2,
    accent: 0xd8fff9,
    ink: 0xffffff,
    keeper: 0xff8a3d,
    pattern: "quarters",
  },
  temporadaDiamante: {
    id: "temporadaDiamante",
    label: "Temporada Diamante",
    primary: 0x0d1b3d,
    secondary: 0x8fb4ff,
    accent: 0xe6efff,
    ink: 0xffffff,
    keeper: 0xffd35a,
    pattern: "cross",
  },
  temporadaLeyenda: {
    id: "temporadaLeyenda",
    label: "Temporada Leyenda",
    primary: 0x1a0a1f,
    secondary: 0xff7ad9,
    accent: 0xffc6ef,
    ink: 0xff7ad9,
    keeper: 0x4ade80,
    pattern: "band",
  },
  campeonMundial: {
    id: "campeonMundial",
    label: "Campeón del mundo",
    primary: 0xf6f6f2,
    secondary: 0xd4a017,
    accent: 0xffe082,
    ink: 0x14141a,
    keeper: 0x14141a,
    pattern: "sash",
  },
};

export const KIT_IDS = Object.keys(KITS) as readonly string[];

/**
 * Las de COMPETICION: se ganan acabando una temporada de las estadisticas
 * mundiales en una division (desde Oro), o siendo el primero. Las da el
 * servidor, a partir de partidos que el mismo arbitro: no se pueden falsear.
 */
export const COMPETITIVE_KITS: ReadonlyArray<{ kitId: string; tier?: "oro" | "platino" | "diamante" | "leyenda"; champion?: true }> = [
  { kitId: "temporadaOro", tier: "oro" },
  { kitId: "temporadaPlatino", tier: "platino" },
  { kitId: "temporadaDiamante", tier: "diamante" },
  { kitId: "temporadaLeyenda", tier: "leyenda" },
  { kitId: "campeonMundial", champion: true },
];
export const COMPETITIVE_KIT_IDS: readonly string[] = COMPETITIVE_KITS.map((k) => k.kitId);

/**
 * Las de PREMIO: solo se ganan con hitos de la carrera, y nadie las viste en
 * el mundo hasta que las ganas. Por eso ni el rival "Automatica" ni la
 * segunda equipacion de un club pueden salir con una de estas: seria
 * enseñar el premio antes de darlo. Como se gana cada una esta en la carrera
 * (`career/kits.ts`); aqui solo que existen.
 */
export const REWARD_KIT_IDS: readonly string[] = [
  "burdeosOro",
  "negroOro",
  "blancoOro",
  "medianoche",
  "lava",
  "electrico",
  "arlequin",
  "hielo",
  "radiactivo",
  "tricolor",
];
/** Las que se GANAN (carrera o competicion): ni el rival "Automatica" ni una segunda equipacion las llevan. */
const REWARD_SET = new Set([...REWARD_KIT_IDS, ...COMPETITIVE_KIT_IDS]);

export function isRewardKit(id: string): boolean {
  return REWARD_SET.has(id);
}

export const KIT_PATTERNS = [
  "plain",
  "stripes",
  "halves",
  "sash",
  "hoops",
  "quarters",
  "chevron",
  "band",
  "cross",
] as const satisfies readonly KitPattern[];

/** Id de la equipacion a medida. No vive en `KITS`: se arma con tus colores. */
export const CUSTOM_KIT_ID = "custom";

export interface CustomKitColors {
  primary: number;
  secondary: number;
  accent: number;
  pattern: KitPattern;
}

export const DEFAULT_CUSTOM_KIT: CustomKitColors = {
  primary: 0x1f7a5c,
  secondary: 0xf6f6f2,
  accent: 0xffd76e,
  pattern: "stripes",
};

/**
 * Luminancia percibida (0..1). Es la formula de siempre (BT.601), y se usa para
 * UNA cosa concreta: decidir si el dorsal va en negro o en blanco. Un "10"
 * blanco sobre camiseta amarilla no se lee, y ese es justo el fallo que
 * convierte una equipacion bonita en una injugable.
 */
export function luminance(color: number): number {
  const r = (color >> 16) & 0xff;
  const g = (color >> 8) & 0xff;
  const b = color & 0xff;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

/** Arma la equipacion a medida, eligiendo sola el color del dorsal. */
export function customKit(colors: CustomKitColors): Kit {
  const ink = luminance(colors.primary) > 0.55 ? 0x14141a : 0xffffff;
  return {
    id: CUSTOM_KIT_ID,
    label: "A medida",
    primary: colors.primary,
    secondary: colors.secondary,
    accent: colors.accent,
    ink,
    // El portero se saca del acento invertido: nunca coincide con la camiseta
    // de campo, que es la unica regla que de verdad importa aqui.
    keeper: luminance(colors.primary) > 0.5 ? 0x14141a : colors.accent,
    pattern: colors.pattern,
  };
}

export function kitById(id: string | undefined): Kit {
  if (id && id in KITS) return KITS[id]!;
  return KITS[DEFAULT_HOME_KIT]!;
}

/** Distancia de color en RGB. Basta para decidir si dos camisetas se pisan. */
export function colorDistance(a: number, b: number): number {
  const dr = ((a >> 16) & 0xff) - ((b >> 16) & 0xff);
  const dg = ((a >> 8) & 0xff) - ((b >> 8) & 0xff);
  const db = (a & 0xff) - (b & 0xff);
  return Math.hypot(dr, dg, db);
}

/**
 * La segunda equipacion del rival.
 *
 * Es el arbitro de la unica regla innegociable: si eliges verdiblanco y el
 * rival tambien sale verdiblanco, el partido es ilegible. Se escoge del
 * catalogo la camiseta MAS lejana en color a la tuya, asi que da igual lo que
 * elijas: siempre hay contraste.
 */
export function contrastKitFor(kit: Kit): Kit {
  let best = KITS[DEFAULT_AWAY_KIT]!;
  let bestScore = -1;
  for (const id of KIT_IDS) {
    const candidate = KITS[id]!;
    // Ni la propia ni una de premio (esas se ganan, no se reparten).
    if (candidate.id === kit.id || REWARD_SET.has(id)) continue;
    const score =
      colorDistance(candidate.primary, kit.primary) +
      // Tambien se mira el segundo color: dos camisetas a rayas con el mismo
      // blanco de fondo se confunden aunque el color dominante sea distinto.
      colorDistance(candidate.primary, kit.secondary) * 0.4;
    if (score > bestScore) {
      bestScore = score;
      best = candidate;
    }
  }
  return best;
}

export interface TeamIdentity {
  /** Como se llama el equipo. Sale en el acta, en los goles y en el resumen. */
  name: string;
  /** Tres letras para sitios estrechos (marcador). */
  short: string;
  kit: Kit;
}

/**
 * La identidad viva de los dos equipos.
 *
 * Es un objeto MUTABLE, igual que `TEAM_INSTRUCTIONS`: las capas de render lo
 * leen cada frame, asi que cambiar de equipacion es escribir aqui y ya. Si se
 * reemplazara el objeto entero, quien guardo una referencia se quedaria
 * pintando la camiseta vieja para siempre.
 */
export const TEAM_IDENTITY: Record<TeamSide, TeamIdentity> = {
  home: { name: "LOCAL", short: "LOC", kit: KITS[DEFAULT_HOME_KIT]! },
  away: { name: "VISITANTE", short: "VIS", kit: KITS[DEFAULT_AWAY_KIT]! },
};

/**
 * Abreviatura de tres letras para el marcador.
 *
 * Las reglas son las de las siglas de toda la vida, y estan en este orden
 * porque cortar por lo bruto a tres caracteres pierde justo la parte que
 * identifica al equipo: "Los Mios" se quedaria en "LOS", que no dice nada.
 */
export function shortNameFor(name: string): string {
  const clean = name.trim().toUpperCase();
  if (!clean) return "---";
  const words = clean.split(/\s+/);
  // Tres palabras o mas: iniciales, como cualquier sigla.
  if (words.length >= 3) return words.slice(0, 3).map((w) => w[0]!).join("");
  if (words.length === 2) {
    const [first, second] = words as [string, string];
    // Un prefijo corto (FC, AT, RC) se conserva entero; el resto aporta dos.
    return (first.length <= 2 ? first : first.slice(0, 2)) + second[0]!;
  }
  return clean.slice(0, 3);
}

export function setTeamIdentity(side: TeamSide, name: string, kit: Kit): void {
  const clean = name.trim().slice(0, 16);
  const fallback = side === "home" ? "LOCAL" : "VISITANTE";
  TEAM_IDENTITY[side].name = clean || fallback;
  TEAM_IDENTITY[side].short = shortNameFor(clean || fallback);
  TEAM_IDENTITY[side].kit = kit;
}

/** Colores del jugador segun su bando y si es portero. */
export function kitColorsFor(side: TeamSide, isKeeper: boolean): {
  body: number;
  secondary: number;
  accent: number;
  ink: number;
  pattern: KitPattern;
} {
  const kit = TEAM_IDENTITY[side].kit;
  if (isKeeper) {
    // El portero va de otro color y SIN patron: la regla real es que se
    // distinga de los veintiuno de campo de un vistazo.
    return {
      body: kit.keeper,
      secondary: kit.keeper,
      accent: kit.accent,
      ink: luminance(kit.keeper) > 0.55 ? 0x14141a : 0xffffff,
      pattern: "plain",
    };
  }
  return {
    body: kit.primary,
    secondary: kit.secondary,
    accent: kit.accent,
    ink: kit.ink,
    pattern: kit.pattern,
  };
}

// ---------------------------------------------------------------- guardado

export const TEAM_SETUP_STORAGE_KEY = "golazo.teamSetup";

export interface TeamSetup {
  homeName: string;
  awayName: string;
  homeKitId: string;
  /** `"auto"` = la mas contrastada con la tuya. */
  awayKitId: string;
  custom: CustomKitColors;
}

/**
 * "Que el rival se ponga lo que contraste". Sigue existiendo para no romper
 * ajustes ya guardados, pero YA NO ES EL VALOR POR DEFECTO.
 *
 * Lo era, y producia un comportamiento que se lee como un fallo: al elegir tu
 * equipacion, la del rival cambiaba sola en el mismo gesto. La intencion era
 * buena —que los dos equipos nunca se parezcan— pero el precio es que el
 * jugador toca una cosa y se mueven dos, sin haberlo pedido.
 *
 * Ahora cada bando es una eleccion estable e independiente. El contraste se
 * resuelve donde toca: eligiendo un rival por defecto que ya contrasta con el
 * local por defecto, y dejando que la persona decida a partir de ahi.
 */
export const AUTO_AWAY_KIT = "auto";


export const DEFAULT_TEAM_SETUP: TeamSetup = {
  homeName: "",
  awayName: "",
  homeKitId: DEFAULT_HOME_KIT,
  awayKitId: DEFAULT_AWAY_KIT,
  custom: { ...DEFAULT_CUSTOM_KIT },
};

function sanitizeColor(v: unknown, fallback: number): number {
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(0xffffff, Math.max(0, Math.round(n)));
}

export function normalizeTeamSetup(raw: Partial<TeamSetup> | null | undefined): TeamSetup {
  const custom = raw?.custom ?? DEFAULT_CUSTOM_KIT;
  const pattern = (KIT_PATTERNS as readonly string[]).includes(custom.pattern ?? "")
    ? custom.pattern
    : DEFAULT_CUSTOM_KIT.pattern;
  const known = (id: string | undefined, fallback: string): string =>
    id === CUSTOM_KIT_ID || id === AUTO_AWAY_KIT || (id !== undefined && id in KITS)
      ? id
      : fallback;
  return {
    homeName: (raw?.homeName ?? "").slice(0, 16),
    awayName: (raw?.awayName ?? "").slice(0, 16),
    // El rival no puede ser "auto" del lado de casa ni la casa "auto" nunca.
    homeKitId: known(raw?.homeKitId, DEFAULT_HOME_KIT) === AUTO_AWAY_KIT
      ? DEFAULT_HOME_KIT
      : known(raw?.homeKitId, DEFAULT_HOME_KIT),
    awayKitId: known(raw?.awayKitId, DEFAULT_AWAY_KIT),
    custom: {
      primary: sanitizeColor(custom.primary, DEFAULT_CUSTOM_KIT.primary),
      secondary: sanitizeColor(custom.secondary, DEFAULT_CUSTOM_KIT.secondary),
      accent: sanitizeColor(custom.accent, DEFAULT_CUSTOM_KIT.accent),
      pattern,
    },
  };
}

/** La equipacion de casa que describe este ajuste (resuelve "a medida"). */
export function homeKitOf(setup: TeamSetup): Kit {
  return setup.homeKitId === CUSTOM_KIT_ID ? customKit(setup.custom) : kitById(setup.homeKitId);
}

/** La del rival, resolviendo "auto" contra la de casa. */
export function awayKitOf(setup: TeamSetup): Kit {
  const home = homeKitOf(setup);
  // "auto" solo sobrevive en ajustes guardados de antes; nadie lo elige ya.
  if (setup.awayKitId === AUTO_AWAY_KIT) return contrastKitFor(home);
  // La equipacion a medida es UNA, la del jugador. Si el rival la tuviera
  // puesta los dos equipos serian identicos, asi que se le da su contraste.
  if (setup.awayKitId === CUSTOM_KIT_ID) return contrastKitFor(home);
  return kitById(setup.awayKitId);
}

/** Vuelca el ajuste sobre la identidad viva. Es lo que "aplica" el cambio. */
export function applyTeamSetup(setup: TeamSetup): void {
  setTeamIdentity("home", setup.homeName, homeKitOf(setup));
  setTeamIdentity("away", setup.awayName, awayKitOf(setup));
}

export function loadStoredTeamSetup(): TeamSetup {
  try {
    const raw = localStorage.getItem(TEAM_SETUP_STORAGE_KEY);
    if (!raw) return normalizeTeamSetup(null);
    return normalizeTeamSetup(JSON.parse(raw) as Partial<TeamSetup>);
  } catch {
    return normalizeTeamSetup(null);
  }
}

export function saveStoredTeamSetup(setup: TeamSetup): void {
  try {
    localStorage.setItem(TEAM_SETUP_STORAGE_KEY, JSON.stringify(setup));
  } catch {
    // Modo privado o cuota llena: se juega igual, sin recordar la camiseta.
  }
}
